import { createOpenAIClient } from '~/utils/openaiClient';
import { reportError } from '~/utils/reportError';

import {
  RATE_PLAN_MATCH_RESPONSE_SCHEMA,
  RATE_PLAN_MATCH_SYSTEM_PROMPT,
  buildRatePlanMatchUserPrompt,
  parseRatePlanMatchResponse,
} from './ratePlanMatchPrompt';
import type { RatePlanMatchRequest } from './ratePlanMatchPrompt';

const RATE_PLAN_MATCH_MODEL = 'gpt-4.1-mini';

export interface RatePlanMatchesResult {
  /** True when the AI call itself failed, so an empty `matches` means "unknown", not "nothing matched". */
  hasFailed: boolean;
  /** Package id -> the exact rate plan name it was matched to, for matches the AI was confident in. */
  matches: Record<string, string>;
}

// One batched call over every package whose room has several rate plans. Only a high-confidence answer that is one
// of that package's own candidates is trusted; the rest are left for the CM to choose.
export async function resolveRatePlanMatchesWithAi(
  requests: Array<RatePlanMatchRequest>,
): Promise<RatePlanMatchesResult> {
  if (requests.length === 0) {
    return { hasFailed: false, matches: {} };
  }

  try {
    const client = createOpenAIClient();
    const response = await client.responses.create(
      {
        model: RATE_PLAN_MATCH_MODEL,
        input: [
          { role: 'system', content: RATE_PLAN_MATCH_SYSTEM_PROMPT },
          { role: 'user', content: buildRatePlanMatchUserPrompt(requests) },
        ],
        text: {
          format: {
            type: 'json_schema',
            name: 'rate_plan_matches',
            schema: RATE_PLAN_MATCH_RESPONSE_SCHEMA,
            strict: true,
          },
        },
      },
      // The proxy gives up after 60s with a 504, which the SDK would retry; the CM can pick instead of waiting again.
      { maxRetries: 0 },
    );

    const parsedMatches = parseRatePlanMatchResponse(response.output_text || '');

    if (!parsedMatches) {
      reportError(new Error('Rate plan matcher returned an unparseable response'));
      return { hasFailed: true, matches: {} };
    }

    const ratePlanNamesByPackageId = new Map(
      requests.map(({ packageId, ratePlanNames }) => [packageId, ratePlanNames.map((name) => name.trim())]),
    );
    const matches = parsedMatches.reduce<Record<string, string>>(
      (resolved, { confidence, packageId, ratePlanName }) => {
        const matchedName = ratePlanName?.trim();

        if (confidence === 'high' && matchedName && ratePlanNamesByPackageId.get(packageId)?.includes(matchedName)) {
          resolved[packageId] = matchedName;
        }

        return resolved;
      },
      {},
    );

    return { hasFailed: false, matches };
  } catch (error) {
    reportError(error);
    return { hasFailed: true, matches: {} };
  }
}
